/**
 * The ordered record of every executed request: what the Console tab shows,
 * and the input to replay.
 *
 * While a session merges (a gizmo drag at 60 Hz), its entry is *replaced*
 * rather than appended, so one drag is one journal line (review F-7). The
 * merged request carries the last payload, which is exactly what replay needs.
 */
import type { ChangeScheduler } from "../core/ChangeScheduler";
import { VersionedStore } from "../core/store";
import type { CommandRequest } from "./types";

export interface JournalEntry {
  readonly seq: number;
  readonly request: CommandRequest;
  /** The definition's `describe()` label at execution time. */
  readonly label: string;
}

export interface JournalSnapshot {
  readonly entries: readonly JournalEntry[];
}

export class CommandJournal {
  readonly store: VersionedStore<JournalSnapshot>;
  private nextSeq = 1;

  constructor(
    scheduler: ChangeScheduler,
    private readonly capacity: number,
  ) {
    this.store = new VersionedStore<JournalSnapshot>(scheduler, { entries: [] });
  }

  append(request: CommandRequest, label: string): JournalEntry {
    const entry: JournalEntry = { seq: this.nextSeq++, request, label };
    const entries = this.store.getSnapshot().entries;
    const start = Math.max(0, entries.length + 1 - this.capacity);
    this.store.publish({ entries: [...entries.slice(start), entry] });
    return entry;
  }

  /** Replaces a merged entry's request in place. No-op if it has scrolled out. */
  replace(seq: number, request: CommandRequest, label: string): void {
    const entries = this.store.getSnapshot().entries;
    const index = entries.findIndex((entry) => entry.seq === seq);
    if (index === -1) return;

    const next = [...entries];
    next[index] = { seq, request, label };
    this.store.publish({ entries: next });
  }
}
